export const M6_UNIT_6 = {
  id: "m6-u6-could-suggestions",
  title: "Unidad 6: Making Suggestions with COULD",
  topic: "Master gentle suggestions! COULD is softer than SHOULD - perfect for offering options without being pushy. Learn to suggest ideas politely and naturally!",
  iconType: "comments",
  grammarImage: "/grammar/grammar-m6-u6.jpg",
  grammarText: "Use COULD to offer gentle suggestions and possibilities. Softer than SHOULD (advice) and MUST (obligation). Perfect for brainstorming and giving options!",
  sets: [

    // ===================================================================
    // SET 1: INTRODUCING COULD - GENTLE SUGGESTIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s1",
      title: "Set 1: What is COULD?",
      questions: [
        {
          type: "mc",
          q: "Your friend wants ideas for dinner. Which sounds LEAST pushy?",
          options: ["You could try the new Italian place.", "You should try the new Italian place.", "You must try the new Italian place."],
          a: "You could try the new Italian place.",
          tip: "COULD = gentle suggestion (softest option)",
          successtip: "✓COULD!\nCOULD is the 'softest' suggestion modal 🤗\nPerfect when you don't want to push someone!"
        },
        {
          type: "order",
          q: "Context: Tu amigo no sabe qué hacer este fin de semana. Traduce: Podrías ir al cine.",
          words: ["You", "could", "go", "to", "the", "movies."],
          a: "You could go to the movies.",
          tip: "COULD = podrías (gentle suggestion, not strong advice)"
        },
        {
          type: "match",
          q: "Match suggestion strength (soft to strong)",
          pairs: [
            { left: "Softest", right: "You could try..." },
            { left: "Medium", right: "You should try..." },
            { left: "Strongest", right: "You must try..." }
          ],
          a: "completed",
          successtip: "✓STRENGTH SCALE!\nCOULD → SHOULD → MUST\nSuggestion → Advice → Obligation"
        },
        {
          type: "input",
          q: "I'm bored. What can I do? You ___ watch a movie.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "Which shows you're just giving OPTIONS (not telling them what to do)?",
          options: ["You could...", "You should...", "You have to..."],
          a: "You could...",
          tip: "COULD = offering options (not commanding)",
          successtip: "✓OPTIONS!\nCOULD = 'here's one possibility' 💡\nNot forcing them to do it!"
        },
        {
          type: "order",
          q: "Context: Alguien pregunta dónde comer. Traduce: Podrías probar ese restaurante nuevo.",
          words: ["You", "could", "try", "that", "new", "restaurant."],
          a: "You could try that new restaurant.",
          tip: "Restaurant suggestion = COULD (gentle)"
        },
        {
          type: "mc",
          q: "FORM: COULD + ___",
          options: ["base verb", "to + verb", "verb + ing"],
          a: "base verb",
          tip: "COULD + BASE FORM (like all modals)"
        },
        {
          type: "input",
          q: "I don't know what to wear. You ___ wear your blue dress.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓FASHION SUGGESTIONS!\nCOULD is perfect for clothes/style suggestions 👗\nNon-judgmental!"
        },
        {
          type: "mc",
          q: "COULD doesn't change with subject. Which is correct?",
          options: ["He could / She could / They could (all correct)", "He coulds / She coulds", "He could / She coulds"],
          a: "He could / She could / They could (all correct)",
          tip: "COULD = same for all subjects (like all modals)"
        },
        {
          type: "order",
          q: "Context: Tu amigo busca ideas para un regalo. Traduce: Podrías comprarle un libro.",
          words: ["You", "could", "buy", "him", "a", "book."],
          a: "You could buy him a book.",
          successtip: "✓GIFT IDEAS!\nCOULD is ideal for gift suggestions 🎁\nDoesn't pressure them!"
        }
      ]
    },

    // ===================================================================
    // SET 2: COULD vs SHOULD - CRITICAL DIFFERENCE (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s2",
      title: "Set 2: COULD vs SHOULD",
      questions: [
        {
          type: "mc",
          q: "Your friend is sick. Which is stronger advice?",
          options: ["You should see a doctor.", "You could see a doctor.", "Both the same."],
          a: "You should see a doctor.",
          tip: "SHOULD = stronger advice! COULD = gentler suggestion",
          successtip: "✓STRENGTH!\nSHOULD = what I think is best 💡\nCOULD = just one option 🤷\nBig difference for sick people!"
        },
        {
          type: "mc",
          q: "BRAINSTORMING ideas for vacation. Which is better?",
          options: ["You could go to Paris or Rome.", "You should go to Paris or Rome.", "You must go to Paris."],
          a: "You could go to Paris or Rome.",
          tip: "BRAINSTORMING = COULD! (just possibilities)",
          successtip: "✓BRAINSTORMING!\nCOULD is perfect for generating options 🗺️\nNo pressure to choose!"
        },
        {
          type: "order",
          q: "Context: Dando sugerencia suave sobre comida. Traduce: Podrías probar la comida tailandesa.",
          words: ["You", "could", "try", "Thai", "food."],
          a: "You could try Thai food.",
          tip: "Food suggestion = COULD (gentle, not pushy)"
        },
        {
          type: "mc",
          q: "Your friend failed a test. Which is ADVICE (what's best)?",
          options: ["You should study harder.", "You could study harder.", "Both the same."],
          a: "You should study harder.",
          tip: "SHOULD = advice (what's best). COULD = just an option",
          successtip: "✓ADVICE vs SUGGESTION!\nSHOULD = I recommend this 👍\nCOULD = maybe consider this 💭"
        },
        {
          type: "input",
          q: "I want to lose weight. (giving options): You ___ join a gym or work out at home.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Multiple options = COULD! (not should = one advice)"
        },
        {
          type: "order",
          q: "Context: Tu amigo pregunta qué hacer en una ciudad nueva. Traduce: Podrías visitar el museo o caminar por el parque.",
          words: ["You", "could", "visit", "the", "museum", "or", "walk", "through", "the", "park."],
          a: "You could visit the museum or walk through the park.",
          successtip: "✓WALK THROUGH!\n'Walk through' = caminar por (un lugar) 🚶\nTravel phrasal verb!"
        },
        {
          type: "mc",
          q: "SERIOUS PROBLEM: 'My car is broken.' Response:",
          options: ["You should take it to a mechanic.", "You could take it to a mechanic.", "Both equal."],
          a: "You should take it to a mechanic.",
          tip: "SERIOUS PROBLEM = SHOULD (stronger advice needed)",
          successtip: "✓CONTEXT MATTERS!\nSerious issues = SHOULD ⚠️\nCasual ideas = COULD 💭\nChoose based on importance!"
        },
        {
          type: "match",
          q: "Match situation to better modal",
          pairs: [
            { left: "Health emergency", right: "SHOULD" },
            { left: "Weekend activity ideas", right: "COULD" },
            { left: "Financial advice", right: "SHOULD" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "Which sounds more POLITE and less pushy?",
          options: ["You could check out that restaurant.", "You should check out that restaurant.", "You must check out that restaurant."],
          a: "You could check out that restaurant.",
          successtip: "✓CHECK OUT!\n'Check out' = visitar/explorar 🔍\nSuper casual phrasal verb!\n'Check out a place/restaurant/movie/website'"
        },
        {
          type: "order",
          q: "Context: Sugiriendo actividades casuales para el fin de semana. Traduce: Podrías relajarte en casa o salir con amigos.",
          words: ["You", "could", "chill", "at", "home", "or", "hang", "out", "with", "friends."],
          a: "You could chill at home or hang out with friends.",
          successtip: "✓CHILL!\n'Chill' = relajarse 😎\n'Hang out' = pasar tiempo con amigos 👥\nCasual suggestions vocabulary!"
        }
      ]
    },

    // ===================================================================
    // SET 3: COULD with OR - MULTIPLE OPTIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s3",
      title: "Set 3: Offering Choices",
      questions: [
        {
          type: "mc",
          q: "PATTERN: 'You could ___ or you could ___'",
          options: ["Offers multiple options to choose from", "Forces one choice", "Means both are required"],
          a: "Offers multiple options to choose from",
          tip: "COULD + OR = giving choices (not forcing)",
          successtip: "✓CHOICES!\nCOULD...or...COULD = brainstorming pattern 🤔\nVery common in English conversation!"
        },
        {
          type: "order",
          q: "Context: Alguien pregunta qué comer. Traduce: Podrías pedir pizza o cocinar pasta.",
          words: ["You", "could", "order", "pizza", "or", "cook", "pasta."],
          a: "You could order pizza or cook pasta.",
          tip: "Two food options = COULD...or..."
        },
        {
          type: "input",
          q: "Complete: We could catch a movie ___ grab dinner.",
          a: "or",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓CATCH A MOVIE!\n'Catch a movie' = ir al cine 🎬\nVery natural expression!\nNot 'see a movie' (less common)"
        },
        {
          type: "mc",
          q: "I need a break. You suggest TWO options:",
          options: ["You could take a walk or read a book.", "You could take a walk.", "You should take a walk."],
          a: "You could take a walk or read a book.",
          tip: "Multiple options = COULD + OR"
        },
        {
          type: "order",
          q: "Context: Sugiriendo formas de aprender inglés. Traduce: Podrías ver series o escuchar podcasts.",
          words: ["You", "could", "watch", "series", "or", "listen", "to", "podcasts."],
          a: "You could watch series or listen to podcasts.",
          successtip: "✓LEARNING OPTIONS!\nWatch series = ver series 📺\nListen to podcasts = escuchar podcasts 🎧\nModern learning methods!"
        },
        {
          type: "mc",
          q: "SHORTENING: 'You could cook or you could order' → Natural:",
          options: ["You could cook or order", "You could cook or you order", "You cook or could order"],
          a: "You could cook or order",
          tip: "Drop second 'you could' = more natural!",
          successtip: "✓NATURAL SHORTENING!\nYou could X or (you could) Y ✓\nSecond 'you could' is optional!\nNatives drop it constantly!"
        },
        {
          type: "input",
          q: "Travel suggestion: You could fly ___ take the train.",
          a: "or",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Context: Opciones para pasar la tarde. Traduce: Podrías ir de compras o explorar el centro.",
          words: ["You", "could", "go", "shopping", "or", "explore", "downtown."],
          a: "You could go shopping or explore downtown.",
          successtip: "✓EXPLORE!\n'Explore' = explorar 🗺️\n'Downtown' = el centro (ciudad) 🏙️\nTravel vocabulary!"
        },
        {
          type: "mc",
          q: "THREE options: 'You could A, B, ___ C.'",
          options: ["or", "and", "but"],
          a: "or",
          tip: "Multiple options = separate with OR",
          successtip: "✓MULTIPLE OPTIONS!\nA, B, or C = three choices 🎯\nCOULD works with any number of options!"
        },
        {
          type: "match",
          q: "Match contexts to COULD + OR suggestions",
          pairs: [
            { left: "Dinner options", right: "cook or order" },
            { left: "Weekend plans", right: "stay in or go out" },
            { left: "Learning methods", right: "read books or watch videos" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 4: COULD - SUGGESTIONS vs POSSIBILITIES (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s4",
      title: "Set 4: Two Meanings of COULD",
      questions: [
        {
          type: "mc",
          q: "MEANING: 'You could try yoga' = ?",
          options: ["Suggestion (I'm suggesting this)", "Past ability (you were able to)", "Future certain"],
          a: "Suggestion (I'm suggesting this)",
          tip: "COULD = suggestion (one of its meanings)"
        },
        {
          type: "mc",
          q: "MEANING: 'It could rain later' = ?",
          options: ["Possibility (maybe it will)", "Suggestion (I suggest rain)", "Certain future"],
          a: "Possibility (maybe it will)",
          tip: "COULD = possibility (like MIGHT)",
          successtip: "✓TWO MEANINGS!\nCOULD for suggestions (this unit) 💡\nCOULD for possibilities (like might) 🤔\nContext shows which!"
        },
        {
          type: "order",
          q: "Context: Dando sugerencia de actividad. Traduce: Podrías probar ese restaurante nuevo.",
          words: ["You", "could", "try", "that", "new", "restaurant."],
          a: "You could try that new restaurant.",
          tip: "SUGGESTION = COULD (offering idea)"
        },
        {
          type: "mc",
          q: "Which is SUGGESTION (not possibility)?",
          options: ["You could download that app.", "It could snow tonight.", "He could be late."],
          a: "You could download that app.",
          tip: "Download app = suggestion! Weather/lateness = possibility",
          successtip: "✓DOWNLOAD!\n'Download' = descargar 📲\nTech vocabulary infiltrated!"
        },
        {
          type: "input",
          q: "SUGGESTION: I'm stressed. You ___ take a break. (advice)",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Addressing 'you' = usually suggestion"
        },
        {
          type: "order",
          q: "Context: Posibilidad sobre el clima. Traduce: Podría llover mañana.",
          words: ["It", "could", "rain", "tomorrow."],
          a: "It could rain tomorrow.",
          tip: "POSSIBILITY = COULD (like might)"
        },
        {
          type: "mc",
          q: "CLUE: If sentence starts with 'You could...', it's usually:",
          options: ["Suggestion", "Possibility", "Past ability"],
          a: "Suggestion",
          tip: "'You could' = almost always suggestion!",
          successtip: "✓PATTERN!\n'You could...' = 95% suggestion 💡\n'It/He/She could...' = often possibility 🤔\nQuick identification trick!"
        },
        {
          type: "match",
          q: "Match sentence to meaning",
          pairs: [
            { left: "You could learn Spanish", right: "Suggestion" },
            { left: "She could be sleeping", right: "Possibility" },
            { left: "We could visit the museum", right: "Suggestion" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "Which is giving someone an IDEA (suggestion)?",
          options: ["You could stream that show online.", "The internet could be down.", "Traffic could be bad."],
          a: "You could stream that show online.",
          successtip: "✓STREAM!\n'Stream' = ver en streaming 📺\n'Online' = en línea 💻\nModern entertainment vocabulary!"
        },
        {
          type: "order",
          q: "Context: Sugiriendo forma de contactar a alguien. Traduce: Podrías enviarle un mensaje.",
          words: ["You", "could", "send", "him", "a", "message."],
          a: "You could send him a message.",
          successtip: "✓SEND A MESSAGE!\nModern communication 📱\nCOULD for communication suggestions!"
        }
      ]
    },

    // ===================================================================
    // SET 5: TRAVEL & ENTERTAINMENT SUGGESTIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s5",
      title: "Set 5: Travel & Fun Ideas",
      questions: [
        {
          type: "mc",
          q: "'Where should I go on vacation?' Natural response:",
          options: ["You could check out Thailand or Vietnam.", "You should check out Thailand.", "You must check out Thailand."],
          a: "You could check out Thailand or Vietnam.",
          tip: "Travel suggestions = COULD (offering options)",
          successtip: "✓TRAVEL SUGGESTIONS!\nCOULD + check out = perfect combo for travel 🌍\nNon-pushy recommendations!"
        },
        {
          type: "order",
          q: "Context: Alguien busca cosas para hacer en una ciudad. Traduce: Podrías recorrer el centro histórico.",
          words: ["You", "could", "wander", "around", "the", "historic", "center."],
          a: "You could wander around the historic center.",
          successtip: "✓WANDER AROUND!\n'Wander around' = recorrer/pasear sin rumbo fijo 🚶\nTravel phrasal verb!\nMore casual than 'walk around'"
        },
        {
          type: "input",
          q: "'What should I do in Paris?' You ___ visit the Eiffel Tower or explore Montmartre.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓EXPLORE!\n'Explore' = explorar 🗺️\nTravel vocabulary essential!"
        },
        {
          type: "mc",
          q: "Entertainment suggestion: 'I'm bored.' Response:",
          options: ["You could binge-watch a new series.", "You should binge-watch a series.", "You must binge-watch a series."],
          a: "You could binge-watch a new series.",
          tip: "'BINGE-WATCH' = watch many episodes!",
          successtip: "✓BINGE-WATCH!\n'Binge-watch' = ver una serie completa de una sentada 📺\nModern entertainment vocabulary!\nVery common in streaming era!"
        },
        {
          type: "order",
          q: "Context: Sugerencia para encontrar vuelos baratos. Traduce: Podrías reservar con anticipación para ahorrar dinero.",
          words: ["You", "could", "book", "in", "advance", "to", "save", "money."],
          a: "You could book in advance to save money.",
          successtip: "✓BOOK IN ADVANCE!\n'Book in advance' = reservar con anticipación ✈️\n'Save money' = ahorrar dinero 💰\nTravel tips vocabulary!"
        },
        {
          type: "mc",
          q: "'What movie should we watch?' Natural suggestion:",
          options: ["We could catch that new thriller.", "We should catch that thriller.", "We must catch that thriller."],
          a: "We could catch that new thriller.",
          tip: "WE COULD = suggesting for the group",
          successtip: "✓THRILLER!\n'Thriller' = película de suspenso 🎬\nMovie genre vocabulary!"
        },
        {
          type: "input",
          q: "'I want to try local food.' You ___ sample some street food.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓SAMPLE!\n'Sample' = probar (comida) 🍜\nFoodie vocabulary!\n'Sample street food/local cuisine'"
        },
        {
          type: "order",
          q: "Context: Sugerencia de transporte en una ciudad. Traduce: Podrías tomar el metro o alquilar una bici.",
          words: ["You", "could", "take", "the", "subway", "or", "rent", "a", "bike."],
          a: "You could take the subway or rent a bike.",
          successtip: "✓SUBWAY!\n🇺🇸 'Subway' (US) = metro\n🇬🇧 'Underground/Tube' (UK) = metro\nTransportation vocabulary!"
        },
        {
          type: "mc",
          q: "'I need accommodation.' Best suggestion:",
          options: ["You could browse Airbnb or check hotel deals.", "You should browse Airbnb.", "You must book now."],
          a: "You could browse Airbnb or check hotel deals.",
          tip: "BROWSE = look through online!",
          successtip: "✓BROWSE!\n'Browse' = navegar/explorar (online) 🖥️\nInternet vocabulary!\n'Browse websites/listings/options'"
        },
        {
          type: "match",
          q: "Match travel/entertainment situations to suggestions",
          pairs: [
            { left: "Find activities", right: "could wander around" },
            { left: "Watch something", right: "could binge-watch" },
            { left: "Try food", right: "could sample local cuisine" }
          ],
          a: "completed",
          successtip: "✓VOCABULARY BOOST!\nWander, binge-watch, sample, browse, explore, book = learned passively! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 6: PROBLEM-SOLVING WITH COULD (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s6",
      title: "Set 6: Solving Problems",
      questions: [
        {
          type: "mc",
          q: "'My phone keeps freezing.' Helpful suggestion:",
          options: ["You could restart it or update the software.", "You should restart it.", "You must restart it."],
          a: "You could restart it or update the software.",
          tip: "TECH PROBLEM = COULD (offering solutions)",
          successtip: "✓TECH VOCABULARY!\n'Freeze' = congelarse (phone/computer) 📱\n'Restart' = reiniciar 🔄\n'Update' = actualizar 📲\nTech troubleshooting!"
        },
        {
          type: "order",
          q: "Context: Alguien no puede dormir. Traduce: Podrías reducir la cafeína o probar melatonina.",
          words: ["You", "could", "cut", "down", "on", "caffeine", "or", "try", "melatonin."],
          a: "You could cut down on caffeine or try melatonin.",
          successtip: "✓MELATONIN!\n'Melatonin' = melatonina 💊\n'Cut down on' = reducir ☕\nHealth solutions vocabulary!"
        },
        {
          type: "input",
          q: "'I can't figure out this math problem.' You ___ ask the teacher or look up a tutorial.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓FIGURE OUT!\n'Figure out' = resolver/entender 🤔\nProblem-solving phrasal verb!\n'Figure out a problem/solution/answer'"
        },
        {
          type: "mc",
          q: "'My computer is slow.' Two solutions:",
          options: ["You could delete old files or add more RAM.", "You should delete files.", "You must buy a new one."],
          a: "You could delete old files or add more RAM.",
          tip: "MULTIPLE SOLUTIONS = COULD + OR",
          successtip: "✓RAM!\n'RAM' = memoria RAM 💻\n'Delete files' = borrar archivos 🗑️\nComputer vocabulary!"
        },
        {
          type: "order",
          q: "Context: Alguien perdió sus llaves. Traduce: Podrías rastrear tus pasos o revisar tu auto.",
          words: ["You", "could", "retrace", "your", "steps", "or", "check", "your", "car."],
          a: "You could retrace your steps or check your car.",
          successtip: "✓RETRACE YOUR STEPS!\n'Retrace your steps' = regresar sobre tus pasos 👣\nLost item expression!\nVery common advice for finding things!"
        },
        {
          type: "mc",
          q: "'I don't understand this topic.' Suggestion:",
          options: ["You could watch YouTube videos or hire a tutor.", "You should watch videos.", "You must hire a tutor."],
          a: "You could watch YouTube videos or hire a tutor.",
          tip: "LEARNING PROBLEM = COULD (multiple approaches)",
          successtip: "✓HIRE A TUTOR!\n'Hire' = contratar 👨‍🏫\n'Tutor' = tutor/profesor particular 📚\nEducation vocabulary!"
        },
        {
          type: "input",
          q: "'My back hurts from sitting.' You ___ stretch or get a standing desk.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓STANDING DESK!\n'Standing desk' = escritorio de pie 🖥️\nModern office vocabulary!\nHealth-conscious workplace trend!"
        },
        {
          type: "order",
          q: "Context: Problema de wifi. Traduce: Podrías reiniciar el router o contactar a tu proveedor.",
          words: ["You", "could", "reset", "the", "router", "or", "contact", "your", "provider."],
          a: "You could reset the router or contact your provider.",
          successtip: "✓RESET!\n'Reset' = reiniciar/restablecer 🔧\n'Router' = router 📡\n'Provider' = proveedor de internet 💻\nTech support vocabulary!"
        },
        {
          type: "mc",
          q: "'I'm stuck in traffic.' Practical suggestion:",
          options: ["You could take a shortcut or wait it out.", "You should take a shortcut.", "You must turn around."],
          a: "You could take a shortcut or wait it out.",
          tip: "'WAIT IT OUT' = wait until it's over!",
          successtip: "✓WAIT IT OUT!\n'Wait it out' = esperar a que pase ⏰\nPatience phrasal verb!\n'Wait out' traffic/storm/situation"
        },
        {
          type: "match",
          q: "Match problems to COULD solutions",
          pairs: [
            { left: "Phone problems", right: "could restart it" },
            { left: "Lost something", right: "could retrace steps" },
            { left: "Don't understand", right: "could watch tutorials" }
          ],
          a: "completed",
          successtip: "✓PROBLEM-SOLVING VOCABULARY!\nRestart, retrace, figure out, reset, wait out = 5+ new expressions! 🔧"
        }
      ]
    },

    // ===================================================================
    // SET 7: COULD IN DIFFERENT CONTEXTS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s7",
      title: "Set 7: COULD in Context",
      questions: [
        {
          type: "mc",
          q: "PRESENT suggestion: 'I'm free now.' Response:",
          options: ["We could grab coffee.", "We could grabbed coffee.", "We could to grab coffee."],
          a: "We could grab coffee.",
          tip: "COULD + BASE VERB (same form for all times!)",
          successtip: "✓GRAB COFFEE!\n'Grab coffee' = tomar un café (casual) ☕\nModern casual expression!"
        },
        {
          type: "order",
          q: "Context: Sugerencia para más tarde hoy (futuro cercano). Traduce: Podrías pasar por mi casa después del trabajo.",
          words: ["You", "could", "swing", "by", "my", "place", "after", "work."],
          a: "You could swing by my place after work.",
          tip: "'After work' shows future, but COULD stays same!",
          successtip: "✓SWING BY!\n'Swing by' = pasar por 🚗\n'My place' = mi casa (informal) 🏠\nCasual visit vocabulary!"
        },
        {
          type: "input",
          q: "GENERAL suggestion: People ___ save more for retirement. (advice for everyone)",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "HYPOTHETICAL: 'If you had more time...'",
          options: ["...you could learn a new language.", "...you should learn a new language.", "...you will learn a language."],
          a: "...you could learn a new language.",
          tip: "HYPOTHETICAL = COULD works well!",
          successtip: "✓HYPOTHETICALS!\nCOULD fits perfectly with 'if' scenarios 💭\nShowing possibilities!"
        },
        {
          type: "order",
          q: "Context: Hablando de posibilidades a largo plazo. Traduce: Podrías mudarte al extranjero algún día.",
          words: ["You", "could", "move", "abroad", "someday."],
          a: "You could move abroad someday.",
          successtip: "✓MOVE ABROAD!\n'Move abroad' = mudarse al extranjero 🌍\n'Someday' = algún día 📅\nBig life decision vocabulary!"
        },
        {
          type: "mc",
          q: "CONTINUOUS action: 'What are they doing?' 'They could be...'",
          options: ["...sleeping.", "...sleep.", "...slept."],
          a: "...sleeping.",
          tip: "COULD BE + -ING = ongoing possibility",
          successtip: "✓COULD BE -ING!\nCould be sleeping/working/eating 💤\nFor actions happening now (possibility)\nDifferent from suggestion!"
        },
        {
          type: "input",
          q: "WEEKEND plan: This Saturday, we ___ check out that new art gallery.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ART GALLERY!\n'Art gallery' = galería de arte 🎨\nCulture vocabulary!"
        },
        {
          type: "order",
          q: "Context: Sugerencia para el futuro lejano. Traduce: Podrías abrir tu propio negocio en unos años.",
          words: ["You", "could", "start", "your", "own", "business", "in", "a", "few", "years."],
          a: "You could start your own business in a few years.",
          successtip: "✓START A BUSINESS!\n'Start a business' = abrir un negocio 💼\n'In a few years' = en unos años 📅\nCareer vocabulary!"
        },
        {
          type: "mc",
          q: "HABIT suggestion: 'I want to be healthier.'",
          options: ["You could work out three times a week.", "You could worked out.", "You could to work out."],
          a: "You could work out three times a week.",
          tip: "HABITUAL = COULD + frequency phrase"
        },
        {
          type: "match",
          q: "Match time context to COULD usage",
          pairs: [
            { left: "Right now", right: "We could leave" },
            { left: "This weekend", right: "You could visit" },
            { left: "Someday/future", right: "You could move abroad" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 8: POLITE OFFERS WITH COULD (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s8",
      title: "Set 8: Polite Offers",
      questions: [
        {
          type: "mc",
          q: "OFFERING HELP: 'You look lost.' Polite offer:",
          options: ["I could help you find it.", "I should help you.", "I will help you."],
          a: "I could help you find it.",
          tip: "I COULD = polite offer (not forcing help on them)",
          successtip: "✓POLITE OFFERS!\n'I could help...' = gentle offer 🤗\nLess pushy than 'I'll help' (direct)\nMore polite!"
        },
        {
          type: "order",
          q: "Context: Ofreciendo llevar a alguien. Traduce: Podría llevarte si necesitas.",
          words: ["I", "could", "give", "you", "a", "ride", "if", "you", "need."],
          a: "I could give you a ride if you need.",
          tip: "I COULD = offering (not insisting)",
          successtip: "✓GIVE A RIDE!\n'Give you a ride' (US) = llevarte en carro 🚗\n'Give you a lift' (UK) = same!\nTransport offer vocabulary!"
        },
        {
          type: "input",
          q: "'This is heavy!' Polite offer: I ___ carry that for you.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "WE COULD = offering to do something together:",
          options: ["We could split the bill.", "We should split the bill.", "We must split the bill."],
          a: "We could split the bill.",
          tip: "WE COULD = suggesting shared action",
          successtip: "✓SPLIT THE BILL!\n'Split the bill' = dividir la cuenta 💳\nRestaurant vocabulary!\nCommon among friends!"
        },
        {
          type: "order",
          q: "Context: Ofreciendo ayuda con tecnología. Traduce: Podría mostrarte cómo usarlo.",
          words: ["I", "could", "show", "you", "how", "to", "use", "it."],
          a: "I could show you how to use it.",
          successtip: "✓SHOW YOU HOW!\n'Show you how to...' = enseñarte cómo... 👨‍🏫\nHelping/teaching phrase!"
        },
        {
          type: "mc",
          q: "'I don't know where to eat.' Group suggestion:",
          options: ["We could try that new place downtown.", "We should try that place.", "We will try that place."],
          a: "We could try that new place downtown.",
          tip: "WE COULD = group suggestion (not commanding)"
        },
        {
          type: "input",
          q: "'I need to practice English.' Offer: I ___ practice with you sometime.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓PRACTICE WITH!\n'Practice with someone' = practicar con alguien 🗣️\nLanguage exchange offer!"
        },
        {
          type: "order",
          q: "Context: Ofreciendo préstamo de algo. Traduce: Podría prestarte mi portátil si lo necesitas.",
          words: ["I", "could", "lend", "you", "my", "laptop", "if", "you", "need", "it."],
          a: "I could lend you my laptop if you need it.",
          successtip: "✓LEND!\n'Lend' = prestar (algo tuyo a alguien) 🖥️\n'Borrow' = pedir prestado ↔️\nDon't confuse them!"
        },
        {
          type: "mc",
          q: "OFFERING TIME: 'When should we meet?'",
          options: ["We could meet at 3 PM.", "We should meet at 3 PM.", "We must meet at 3 PM."],
          a: "We could meet at 3 PM.",
          tip: "COULD = offering time option (flexible)",
          successtip: "✓FLEXIBLE SCHEDULING!\nCOULD for time suggestions = you're flexible ⏰\nSHOULD = more direct/fixed 📅"
        },
        {
          type: "match",
          q: "Match polite offers",
          pairs: [
            { left: "Transportation", right: "I could give you a ride" },
            { left: "Help", right: "I could help you out" },
            { left: "Sharing cost", right: "We could split it" }
          ],
          a: "completed",
          successtip: "✓HELP OUT!\n'Help out' = ayudar 🤝\nVery casual helping phrase!"
        }
      ]
    },

    // ===================================================================
    // SET 9: COMMON MISTAKES WITH COULD (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s9",
      title: "Set 9: Common Errors",
      questions: [
        {
          type: "mc",
          q: "ERROR: Which is WRONG?",
          options: ["You could to go.", "You could go.", "Could you go?"],
          a: "You could to go.",
          tip: "NEVER 'could to'! COULD + base verb (no 'to')",
          successtip: "✓#1 ERROR!\nCould + base verb ✓\nCould to ❌ WRONG!\nSame rule as all modals!"
        },
        {
          type: "mc",
          q: "FIX: 'She coulds go' → Correct:",
          options: ["She could go", "She could goes", "She could to go"],
          a: "She could go",
          tip: "COULD doesn't change! No 's' for third person"
        },
        {
          type: "input",
          q: "FIX: 'You could going there' → You could ___ there.",
          a: "go",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "COULD + BASE VERB! Not -ing form"
        },
        {
          type: "mc",
          q: "CONFUSION: 'Could' (suggestion) vs 'Could' (past ability):",
          options: ["Different meanings, context shows which", "Always the same", "Can't tell difference"],
          a: "Different meanings, context shows which",
          tip: "COULD = suggestion OR past ability (context matters!)",
          successtip: "✓TWO MEANINGS!\n💡 You could try (suggestion)\n💪 I could swim when I was 5 (past ability)\nContext is key!"
        },
        {
          type: "order",
          q: "CORRECT WAY: Él podría intentarlo. (suggestion)",
          words: ["He", "could", "try", "it."],
          a: "He could try it.",
          tip: "No changes to could! Same for all subjects"
        },
        {
          type: "mc",
          q: "ERROR: 'Do you could go?' → Correct:",
          options: ["Could you go?", "Do you could go?", "You could go?"],
          a: "Could you go?",
          tip: "NO 'do' with could! COULD comes first in questions",
          successtip: "✓QUESTION FORM!\nCould + subject + verb ✓\nDo + could ❌ WRONG!\nModal questions don't use 'do'!"
        },
        {
          type: "input",
          q: "FIX: 'You could can go' → You ___ go.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["can"] },
          tip: "ONE MODAL! Never 'could can' or 'could should'"
        },
        {
          type: "mc",
          q: "TRANSLATION ERROR: 'Podrías' can be:",
          options: ["Could (suggestion) OR Might (possibility)", "Only could", "Only might"],
          a: "Could (suggestion) OR Might (possibility)",
          tip: "Spanish 'podrías' = English could/might (depends!)",
          successtip: "✓TRANSLATION!\n💡 Podrías intentar = You could try (suggestion)\n🤔 Podrías tener razón = You might be right (possibility)\nContext matters!"
        },
        {
          type: "order",
          q: "CORRECT: Podrías llamarla. (suggestion)",
          words: ["You", "could", "call", "her."],
          a: "You could call her.",
          tip: "COULD for suggestion (not might here)"
        },
        {
          type: "match",
          q: "Match errors to corrections",
          pairs: [
            { left: "You could to go", right: "You could go" },
            { left: "She coulds go", right: "She could go" },
            { left: "Do you could go?", right: "Could you go?" }
          ],
          a: "completed",
          successtip: "✓ERROR-FREE!\nYou avoid the top 3 mistakes with COULD! 🎉"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTERY - NATURAL SUGGESTION PATTERNS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u6-s10",
      title: "Set 10: Natural Suggestions",
      questions: [
        {
          type: "mc",
          q: "SOFTENING: 'You could always...' adds:",
          options: ["Makes suggestion even softer (it's an option)", "Makes it stronger", "No difference"],
          a: "Makes suggestion even softer (it's an option)",
          tip: "'ALWAYS' = emphasizes it's one option among many",
          successtip: "✓YOU COULD ALWAYS!\n'You could always...' = siempre podrías... 💭\nExtra soft pattern!\n'You could always try again/ask for help/change your mind'"
        },
        {
          type: "mc",
          q: "TENTATIVE: 'You could maybe...' vs 'You could...':",
          options: ["'Maybe' makes it more tentative", "Exactly the same", "'Maybe' makes it stronger"],
          a: "'Maybe' makes it more tentative",
          tip: "'MAYBE' = adds uncertainty = even gentler!",
          successtip: "✓TENTATIVE SUGGESTIONS!\n'You could maybe try...' = quizás podrías intentar... 🤷\nDouble softening technique!"
        },
        {
          type: "order",
          q: "Context: Sugerencia muy suave sobre cambiar de trabajo. Traduce: Siempre podrías buscar algo mejor.",
          words: ["You", "could", "always", "look", "for", "something", "better."],
          a: "You could always look for something better.",
          successtip: "✓LOOK FOR!\n'Look for' = buscar 🔍\nJob search vocabulary!"
        },
        {
          type: "mc",
          q: "ENCOURAGING: 'You could at least...' means:",
          options: ["Do this as a minimum/starting point", "Do only this and nothing else", "Do this immediately"],
          a: "Do this as a minimum/starting point",
          tip: "'AT LEAST' = minimum effort/starting point",
          successtip: "✓AT LEAST!\n'You could at least try' = al menos podrías intentar 🤷\nEncouraging small steps!\n'Could at least call/check/ask'"
        },
        {
          type: "input",
          q: "ENCOURAGING: You don't have to commit fully. You ___ at least check it out.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'At least' = encouraging minimum action"
        },
        {
          type: "mc",
          q: "BRAINSTORMING PATTERN: 'We could either X or Y' means:",
          options: ["Two options to choose from", "Must do both", "Must do neither"],
          a: "Two options to choose from",
          tip: "'EITHER...OR' = clear choice between two",
          successtip: "✓EITHER...OR!\n'We could either stay or go' ↔️\nClear A or B choice structure!\nVery organized suggestion pattern!"
        },
        {
          type: "order",
          q: "Context: Ofreciendo dos opciones claras. Traduce: Podrías intentar yoga o pilates.",
          words: ["You", "could", "try", "either", "yoga", "or", "pilates."],
          a: "You could try either yoga or pilates.",
          successtip: "✓YOGA & PILATES!\nYoga = yoga 🧘\nPilates = pilates 💪\nFitness vocabulary!"
        },
        {
          type: "mc",
          q: "NATIVE IDIOM: 'You could give it a go' = ?",
          options: ["Try it", "Stop it", "Forget it"],
          a: "Try it",
          tip: "'GIVE IT A GO' = British for 'try it'!",
          successtip: "✓GIVE IT A GO!\n'Give it a go' = intentarlo 🎯\nBritish expression!\nSame as 'give it a try/shot'\nAll mean: intentar"
        },
        {
          type: "match",
          q: "Match softening techniques",
          pairs: [
            { left: "Softest", right: "You could maybe..." },
            { left: "Alternative option", right: "You could always..." },
            { left: "Minimum effort", right: "You could at least..." }
          ],
          a: "completed",
          successtip: "✓SOFTENING MASTERY!\nYou know 3+ ways to make suggestions even gentler! 🤗"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Most natural friendly suggestion:",
          options: ["You could always give it a try - worst case, you learn something!", "You must try this now.", "You could to try this."],
          a: "You could always give it a try - worst case, you learn something!",
          successtip: "✓UNIT 6 COMPLETE! 🎉🏆\n\nYOU MASTERED:\n✓ COULD for gentle suggestions\n✓ COULD vs SHOULD (strength levels)\n✓ COULD + OR (multiple options)\n✓ COULD meanings (suggestion vs possibility)\n✓ Natural softening patterns:\n  • Could always\n  • Could maybe\n  • Could at least\n  • Could either...or\n✓ Polite offers (I/We could...)\n✓ 50+ vocabulary items:\n  • Travel: wander, explore, book, sample\n  • Entertainment: binge-watch, catch, stream\n  • Problem-solving: figure out, reset, retrace\n  • Tech: download, browse, update, restart\n  • Social: split, lend, swing by, grab\n  • And many more!\n✓ Common mistakes corrected\n✓ Context-based usage\n\nYou can now:\n💡 Make gentle suggestions\n🤗 Offer ideas without being pushy\n🎯 Give multiple options naturally\n💬 Use 50+ new vocabulary items\n🗣️ Sound polite and considerate\n🔧 Suggest solutions to problems\n✈️ Make travel recommendations\n📱 Give tech advice\n👥 Offer help politely\n\n¡Increíble trabajo! Módulo 6 casi completo! 🚀\n\nNext: Review module o siguiente módulo!"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - PROGRESSIVE FORMAT (10 preguntas en bloques)
  // ===================================================================
  readingSet: {
    id: "m6-u6-reading",
    title: "Reading Practice - Planning the Perfect Weekend",
    category: "reading",
    
    questions: [
      // BLOQUE 1: Planning Email (preguntas 1-2)
      {
        type: "passage",
        passage: `**Email Thread: Weekend Plans**

**From:** Mike  
**To:** Sarah, Tom, Lisa  
**Subject:** This Saturday - Ideas?

Hey everyone!

We're all free this Saturday, so we should do something fun together! I'm not sure what though. Any suggestions?

We could check out that new art exhibit downtown - I heard it's really good. Or we could go hiking if the weather's nice. I saw there's also a food festival happening in Central Park. We could wander around there and sample different foods.

What do you guys think? I'm open to anything!

— Mike`
      },
      {
        type: "mc",
        q: "How many activity ideas does Mike suggest?",
        options: ["Three (art exhibit, hiking, food festival)", "One", "Two"],
        a: "Three (art exhibit, hiking, food festival)",
        tip: "Mike gives multiple options with COULD"
      },
      {
        type: "mc",
        q: "What phrasal verb means 'walk around casually'?",
        options: ["Wander around", "Check out", "Go hiking"],
        a: "Wander around",
        successtip: "✓WANDER AROUND!\nYou learned this in Set 5! 🚶"
      },

      // BLOQUE 2: Sarah's Response (preguntas 3-4)
      {
        type: "passage",
        passage: `**From:** Sarah  
**To:** Mike, Tom, Lisa  
**Subject:** RE: This Saturday - Ideas?

Love all these ideas! I'm leaning towards the food festival since I'm always hungry 😄

If we go there, we could take the subway to save time, or we could bike if anyone has extra bikes. We could meet at 11 AM and spend the whole afternoon there.

For dinner afterwards, we could either grab something at the festival or sit down at a proper restaurant. I know a great Thai place nearby.

Also, someone should check the weather forecast. If it rains, we could always go to that new arcade that just opened. My cousin said it's awesome - they have VR games and everything!

— Sarah`
      },
      {
        type: "mc",
        q: "Sarah uses 'could either...or' when talking about:",
        options: ["Dinner options", "Transportation", "Meeting time"],
        a: "Dinner options",
        tip: "'Either...or' = clear choice between two options"
      },
      {
        type: "mc",
        q: "What does 'we could always...' suggest about the arcade?",
        options: ["It's a backup option if it rains", "It's the main plan", "They must go there"],
        a: "It's a backup option if it rains",
        successtip: "✓COULD ALWAYS!\n'Could always' = backup/alternative option 🎮\nYou learned this pattern!"
      },

      // BLOQUE 3: Tom's Tech Suggestion (preguntas 5-6)
      {
        type: "passage",
        passage: `**From:** Tom  
**To:** Mike, Sarah, Lisa  
**Subject:** RE: This Saturday - Ideas?

I'm down for the food festival! Quick thought - we could download that food festival app. It shows you a map of all the vendors and you can browse the menus before we get there.

Also, we could split an Uber if taking bikes sounds like too much work. It would only be like $5 each.

One more thing - we could maybe invite Jenny? She mentioned she wanted to hang out more. Just a thought!

— Tom`
      },
      {
        type: "mc",
        q: "Tom uses 'could maybe' which makes his suggestion about Jenny:",
        options: ["Very tentative/soft", "Very strong", "Required"],
        a: "Very tentative/soft",
        successtip: "✓COULD MAYBE!\nDouble softening = extra polite 🤗\nYou learned this in Set 10!"
      },
      {
        type: "mc",
        q: "What vocabulary word means 'look through (online)'?",
        options: ["Browse", "Download", "Split"],
        a: "Browse",
        successtip: "✓BROWSE!\nBrowse menus/websites 🖥️\nTech vocabulary from Set 5!"
      },

      // BLOQUE 4: Lisa's Final Plan (preguntas 7-8)
      {
        type: "passage",
        passage: `**From:** Lisa  
**To:** Mike, Sarah, Tom  
**Subject:** RE: This Saturday - Final Plan!

Okay everyone, I checked the weather and it looks perfect - sunny and 75°F!

Here's what we could do:
1. Meet at Central Park entrance at 11 AM
2. Spend 2-3 hours at the food festival
3. If we're still hungry, grab dinner at Sarah's Thai place
4. If anyone wants, we could catch a movie afterwards

I already downloaded that app Tom mentioned - looks super helpful! And yes, let's definitely invite Jenny.

Could everyone confirm they're in? I want to let Jenny know the final headcount.

— Lisa`
      },
      {
        type: "mc",
        q: "Lisa's numbered list uses 'could' to:",
        options: ["Suggest a organized plan with options", "Give mandatory instructions", "Show past events"],
        a: "Suggest a organized plan with options",
        tip: "COULD in organized plan = still flexible suggestions"
      },
      {
        type: "mc",
        q: "What does 'catch a movie' mean?",
        options: ["Watch a movie (at cinema)", "Download a movie", "Miss a movie"],
        a: "Watch a movie (at cinema)",
        successtip: "✓CATCH A MOVIE!\nYou learned this in Set 3! 🎬\nNatural expression!"
      },

      // PREGUNTAS FINALES (9-10)
      {
        type: "mc",
        q: "Throughout the emails, COULD is used to:",
        options: ["Make gentle suggestions without being pushy", "Give strong commands", "Talk about past abilities"],
        a: "Make gentle suggestions without being pushy",
        successtip: "✓GENTLE SUGGESTIONS!\nAll friends used COULD to stay flexible 🤗\nNobody was bossy or pushy!"
      },
      {
        type: "mc",
        q: "How many different COULD patterns did you see?",
        options: ["Many (could do, could either, could always, could maybe, etc.)", "Only one pattern", "No patterns"],
        a: "Many (could do, could either, could always, could maybe, etc.)",
        successtip: "✓READING COMPLETE! 🎉\n\nCOULD patterns seen:\n✓ You/We could... (basic)\n✓ Could either...or (choice)\n✓ Could always (backup)\n✓ Could maybe (very soft)\n\nVocabulary found:\n• Check out (visit)\n• Wander around (walk casually)\n• Sample (try food)\n• Download (app)\n• Browse (look through)\n• Split (share cost)\n• Grab (get food)\n• Catch a movie (see film)\n• Hang out (spend time)\n\nSuggestion contexts:\n🎨 Activities (art, hiking)\n🍜 Food (festival, restaurant)\n🚇 Transportation (subway, bike, Uber)\n👥 Social (inviting friend)\n⏰ Timing (meeting, schedule)\n\nYou understood natural suggestion-making! 🌟\nFriends planning together = perfect COULD usage!"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (10 preguntas)
  // ===================================================================
  reviewSet: {
    id: "m6-u6-review",
    title: "Unit Review - COULD Mastery",
    category: "review",
    exercises: [
      {
        type: "mc",
        q: "REVIEW: COULD is ___ than SHOULD:",
        options: ["Softer/more gentle", "Stronger", "Exactly the same"],
        a: "Softer/more gentle",
        tip: "COULD = gentle suggestion, SHOULD = stronger advice"
      },
      {
        type: "mc",
        q: "REVIEW: Form - COULD + ___",
        options: ["base verb", "to + verb", "verb + ing"],
        a: "base verb",
        successtip: "✓FORM MASTERED!\nCould + base verb ✓\nAll modals follow this!"
      },
      {
        type: "order",
        q: "REVIEW: Traduce: Podrías probar ese restaurante nuevo.",
        words: ["You", "could", "try", "that", "new", "restaurant."],
        a: "You could try that new restaurant.",
        tip: "Gentle suggestion = COULD"
      },
      {
        type: "mc",
        q: "REVIEW: Multiple options pattern:",
        options: ["You could X or Y", "You could X and Y", "You could X but Y"],
        a: "You could X or Y",
        successtip: "✓OPTIONS!\nCOULD + OR = perfect for choices 🎯"
      },
      {
        type: "mc",
        q: "REVIEW: 'You could always...' means:",
        options: ["It's an alternative option", "You must do it", "It happened in the past"],
        a: "It's an alternative option",
        successtip: "✓ALWAYS!\n'Could always' = backup plan 💡"
      },
      {
        type: "input",
        q: "REVIEW: Phrasal verb learned - 'explore casually': ___ around",
        a: "wander",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓WANDER!\nOne of 50+ vocabulary items learned! 🌟"
      },
      {
        type: "mc",
        q: "REVIEW: Which is WRONG?",
        options: ["You could to go", "You could go", "Could you go?"],
        a: "You could to go",
        successtip: "✓ERROR AVOIDED!\nCould to ❌ Never!"
      },
      {
        type: "match",
        q: "REVIEW: Match COULD uses",
        pairs: [
          { left: "Suggestion", right: "You could try yoga" },
          { left: "Polite offer", right: "I could help you" },
          { left: "Multiple options", right: "We could stay or go" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "REVIEW: COULD vs SHOULD for serious problems:",
        options: ["SHOULD is better (stronger advice)", "COULD is better", "Both equal"],
        a: "SHOULD is better (stronger advice)",
        successtip: "✓CONTEXT!\nSerious = SHOULD ⚠️\nCasual = COULD 💭"
      },
      {
        type: "mc",
        q: "FINAL REVIEW: You learned COULD for:",
        options: ["All of these: suggestions, options, offers, problem-solving", "Only simple suggestions", "Only questions"],
        a: "All of these: suggestions, options, offers, problem-solving",
        successtip: "✓UNIT 6 MASTERY COMPLETE! 🎉🏆\n\nYOU ACHIEVED:\n✓ COULD for gentle suggestions\n✓ COULD vs SHOULD (strength)\n✓ COULD + OR (choices)\n✓ COULD meanings (suggestion vs possibility)\n✓ Softening patterns:\n  • Could always (alternative)\n  • Could maybe (very soft)\n  • Could at least (minimum)\n  • Could either...or (clear choice)\n✓ Polite offers (I/We could)\n✓ 50+ VOCABULARY infiltrated:\n  **Travel:** wander, explore, book, sample\n  **Entertainment:** binge-watch, catch, stream, browse\n  **Problem-solving:** figure out, reset, retrace, wait out\n  **Tech:** download, update, restart\n  **Social:** split, swing by, grab, hang out, lend\n  **Food:** sample, grab, order\n  **And dozens more!\n✓ Common mistakes fixed\n\nYou can now:\n💡 Suggest ideas gently\n🤗 Offer without pushing\n🎯 Give multiple options\n💬 Use 50+ new words naturally\n🗣️ Sound polite & considerate\n🔧 Suggest solutions\n✈️ Make recommendations\n👥 Offer help politely\n📱 Give tech advice\n🍽️ Suggest food/activities\n\n**VOCABULARY BREAKTHROUGH:**\nYou learned 50+ words/expressions PASSIVELY while focusing on COULD! 🌟\n\nThis is how natives learn - through context, not memorization!\n\n¡Módulo 6 COMPLETO! 🎊\n\nREADY FOR:\n- Module review\n- Or Module 7!\n\n🚀 ¡Excelente progreso!"
      }
    ]
  }
};
